import {
    Box,
    Container,
    Typography,
    useTheme,
    alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, Variants } from 'framer-motion';
import React from 'react';
import { getOrDefault } from '../../../../Helpers/Utils';
import { getTextFromDescriptor } from '../../../../Helpers/TextDescriptorUtils';
import { BottomLabelProps, HeroContainerProps, HeroSectionWithBottomLabelProps } from './types';

const HeroContainer = styled(Box)<HeroContainerProps>(({ theme }) => ({
    minHeight: '75vh',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
}));

const ContentBox = styled(Box)(({ theme }) => ({
    position: 'relative',
    zIndex: 3,
    textAlign: 'center',
    maxWidth: '900px',
    padding: theme.spacing(0, 2),
}));

const BottomLabel = styled(Box)<BottomLabelProps>(({ theme, labelBackgroundColor, labelTextColor }) => ({
    position: 'absolute',
    bottom: 0,
    width: '80%',
    background: labelBackgroundColor || (theme.palette.mode === 'dark' 
        ? 'rgba(18, 18, 18, 0.95)' 
        : 'rgba(0, 0, 0, 0.85)'),
    backdropFilter: 'blur(10px)',
    padding: theme.spacing(3, 2),
    color: labelTextColor || theme.palette.common.white,
    textAlign: 'center',
    borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)'}`,
    borderRadius: "16px 16px 0 0",
    boxShadow: theme.palette.mode === 'dark'
        ? '0 -8px 32px rgba(0,0,0,0.6), 0 -2px 8px rgba(0,0,0,0.4)'
        : '0 -8px 32px rgba(0,0,0,0.3), 0 -2px 8px rgba(0,0,0,0.2)',
    zIndex: 4,
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 -12px 40px rgba(0,0,0,0.7), 0 -4px 12px rgba(0,0,0,0.5)'
            : '0 -12px 40px rgba(0,0,0,0.4), 0 -4px 12px rgba(0,0,0,0.3)',
    }
}));

const HeroSectionWithBottomLabel: React.FC<HeroSectionWithBottomLabelProps> = ({ spec, type, index }) => {
    const theme = useTheme();

    // Component configuration
    const componentSpec = getOrDefault(spec, {});
    const backgroundImageSource = getOrDefault(spec.backgroundImageSource, "")
    const backgroundVideoSource = getOrDefault(spec.backgroundVideoSource, "")
    const backgroundColor = getOrDefault(componentSpec.backgroundColor, theme.palette.background.default);
    const accentColor = getOrDefault(componentSpec.accentColor, theme.palette.primary.main);
    
    // Determine if we have background media
    const hasBackgroundMedia = backgroundImageSource || backgroundVideoSource;
    
    // Smart defaults based on background presence
    const defaultTextColor = hasBackgroundMedia 
        ? theme.palette.common.white 
        : theme.palette.text.primary;

    // Hero content - Using TextDescriptor
    const title = getTextFromDescriptor(componentSpec.title, 'Welcome to the Future');
    const subtitle = getTextFromDescriptor(componentSpec.subtitle, 'Experience innovation like never before');
    const description = getTextFromDescriptor(componentSpec.description);

    // Styling with smart defaults
    const titleColor = getOrDefault(componentSpec.titleColor, defaultTextColor);
    const subtitleColor = getOrDefault(componentSpec.subtitleColor, alpha(defaultTextColor, 0.9));
    const descriptionColor = getOrDefault(componentSpec.descriptionColor, alpha(defaultTextColor, 0.85));

    // Bottom label
    const showBottomLabel = componentSpec.showBottomLabel;
    const bottomLabelText = getTextFromDescriptor(componentSpec.bottomLabelText, 'Scroll down to explore more');
    const bottomLabelBackgroundColor = getOrDefault(
        componentSpec.bottomLabelBackgroundColor, 
        theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.95)' : 'rgba(0,0,0,0.85)'
    );
    const bottomLabelTextColor = getOrDefault(componentSpec.bottomLabelTextColor, theme.palette.common.white);

    // Animation variants
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
            }
        }
    };

    const itemVariants: Variants = {
        hidden: {
            opacity: 0,
            y: 50,
            scale: 0.8
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring" as const,
                stiffness: 100,
                damping: 15,
                duration: 0.6
            }
        }
    };

    return (
        <HeroContainer
            id={spec.id}
            sx={{
                background: !hasBackgroundMedia ? backgroundColor : 'transparent',
            }}
        >
            {/* Animated background orbs - only shown if no background media */}
            {!hasBackgroundMedia && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '10%',
                            left: '-5%',
                            width: 600,
                            height: 600,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha(accentColor, 0.15)}, transparent)`,
                            filter: 'blur(80px)',
                            animation: 'float-slow 25s ease-in-out infinite',
                            '@keyframes float-slow': {
                                '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                                '50%': { transform: 'translate(100px, 100px) scale(1.1)' }
                            }
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '10%',
                            right: '-5%',
                            width: 500,
                            height: 500,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha(accentColor, 0.12)}, transparent)`,
                            filter: 'blur(70px)',
                            animation: 'float-reverse-slow 20s ease-in-out infinite',
                            '@keyframes float-reverse-slow': {
                                '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                                '50%': { transform: 'translate(-80px, -80px) scale(1.15)' }
                            }
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            right: '20%',
                            width: 400,
                            height: 400,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha(accentColor, 0.1)}, transparent)`,
                            filter: 'blur(60px)',
                            animation: 'float-diagonal 22s ease-in-out infinite',
                            '@keyframes float-diagonal': {
                                '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                                '50%': { transform: 'translate(-60px, 60px) scale(1.08)' }
                            }
                        }}
                    />
                </>
            )}

            {/* Background Video */}
            {backgroundVideoSource && (
                <>
                    <Box
                        component="video"
                        autoPlay
                        loop
                        muted
                        playsInline
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 0,
                        }}
                    >
                        <source src={backgroundVideoSource} type="video/mp4" />
                    </Box>
                    {/* Overlay for video */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: theme.palette.mode === 'dark' 
                                ? 'rgba(0,0,0,0.5)' 
                                : 'rgba(0,0,0,0.4)',
                            zIndex: 1,
                        }}
                    />
                </>
            )}

            {/* Background Image */}
            {backgroundImageSource && !backgroundVideoSource && (
                <>
                    <Box
                        component="img"
                        src={backgroundImageSource}
                        alt="Hero background"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 0,
                        }}
                    />
                    {/* Overlay for image */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: theme.palette.mode === 'dark' 
                                ? 'rgba(0,0,0,0.5)' 
                                : 'rgba(0,0,0,0.4)',
                            zIndex: 1,
                        }}
                    />
                </>
            )}

            {/* Main Content */}
            <Container maxWidth="lg">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <ContentBox>
                        {/* Hero Title */}
                        <motion.div variants={itemVariants}>
                            <Typography
                                variant="h1"
                                sx={{
                                    color: titleColor,
                                    fontWeight: 900,
                                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5.5rem' },
                                    lineHeight: 1.1,
                                    mb: 2,
                                    textShadow: hasBackgroundMedia 
                                        ? '0 4px 20px rgba(0,0,0,0.7), 0 2px 8px rgba(0,0,0,0.5)'
                                        : 'none',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                {title}
                            </Typography>
                        </motion.div>

                        {/* Hero Subtitle */}
                        <motion.div variants={itemVariants}>
                            <Typography
                                variant="h4"
                                sx={{
                                    color: subtitleColor,
                                    fontWeight: 600,
                                    fontSize: { xs: '1.3rem', sm: '1.6rem', md: '2rem' },
                                    mb: 3,
                                    textShadow: hasBackgroundMedia
                                        ? '0 3px 12px rgba(0,0,0,0.6), 0 2px 4px rgba(0,0,0,0.4)'
                                        : 'none',
                                    letterSpacing: '0.01em',
                                }}
                            >
                                {subtitle}
                            </Typography>
                        </motion.div>

                        {/* Hero Description */}
                        <motion.div variants={itemVariants}>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: descriptionColor,
                                    fontSize: { xs: '1rem', md: '1.2rem' },
                                    lineHeight: 1.8,
                                    mb: 5,
                                    maxWidth: '700px',
                                    mx: 'auto',
                                    textShadow: hasBackgroundMedia
                                        ? '0 2px 8px rgba(0,0,0,0.5)'
                                        : 'none',
                                    fontWeight: 400,
                                }}
                            >
                                {description}
                            </Typography>
                        </motion.div>
                    </ContentBox>
                </motion.div>
            </Container>

            {/* Bottom Label */}
            {showBottomLabel && (
                <BottomLabel
                    labelBackgroundColor={bottomLabelBackgroundColor}
                    labelTextColor={bottomLabelTextColor}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.6 }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                px: 2,
                                gap: 1,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontSize: { xs: '1.2rem', md: '1.6rem' },
                                    fontWeight: 700,
                                    letterSpacing: '0.5px',
                                    color: bottomLabelTextColor,
                                    textShadow: theme.palette.mode === 'dark'
                                        ? '0 2px 8px rgba(0,0,0,0.8)'
                                        : '0 2px 4px rgba(0,0,0,0.4)',
                                }}
                            >
                                {bottomLabelText}
                            </Typography>
                            <Box
                                component={motion.div}
                                animate={{ 
                                    y: [0, 8, 0],
                                }}
                                transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                                sx={{
                                    fontSize: { xs: '1.2rem', md: '1.6rem' },
                                }}
                            >
                                ↓
                            </Box>
                        </Box>
                    </motion.div>
                </BottomLabel>)
            }
        </HeroContainer>
    );
};

export default HeroSectionWithBottomLabel;
